import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'querystring';

import { useAppContext } from 'src/components/app-context';

import ErrorBoundary from '../../components/error-boundary';
import { PageContent } from 'src/components/page';

import { campaignQuery } from './actions';
import { Deals } from './components/deals';
import CampaignHeader from './components/campaign-header';
import CampaignInactive from './components/campaign-inactive';
import CampaignBreadcrumb from './components/breadcrumb';
import CampaignArticles from './components/campaign-articles';

import { DAILY_DEALS_ID } from '../../globals/helpers/campaign/campaign-ids';

import {
  adjustGroupedUrlParams,
  buildRouter,
  getSelectedFilters,
} from './utils';

import { RANGE_OPTIONS } from './constants';

import type { CampaignPageQuery } from 'src/types';
import type { CampaignFilterStore, RouterParams, UrlParams } from './types';

import {
  StyledCampaign,
  StyledCampaignGrouping,
  StyledGutterWrapper,
  StyledProducts,
  StyledProductWrapper,
} from './styles';
import { CampaignFilters } from './components/filters';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { useCampaignPageQuery } from './queries/useCampaignPageQuery';
import Loader from 'src/components/loader';

const CampaignPageLoader = () => {
  const route = useMatchedRoute();
  const { isLoading, isSuccess, data } = useCampaignPageQuery(route?.variables);

  return (
    <PageContent withAds>
      {isLoading ? <Loader /> : null}
      {isSuccess && data ? (
        <CampaignPageComponent
          campaign={data.campaign}
          categoryTree={data.categoryTree}
          campaigns={data.campaigns}
          campaignDeals={data.campaignDeals}
        />
      ) : null}
    </PageContent>
  );
};

const CampaignPageComponent = ({
  campaign,
  categoryTree,
  campaigns,
  campaignDeals,
}: CampaignPageQuery) => {
  const { renderMode, t } = useAppContext();

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [showFilter, setShowFilter] = useState(false);
  const [router, setRouter] = useState<RouterParams>(buildRouter(location));
  const [stores, setStores] = useState<CampaignFilterStore[]>([]);

  const selectedFilters = getSelectedFilters(router, stores, categoryTree, t);
  const featuredArticle = campaign?.articles
    ? campaign?.articles.find(item => item?.featured)
    : null;
  const activeCampaign = campaign?.state === 'ACTIVE';
  const activeCampaigns = campaigns?.filter(
    item => item?.id !== campaign?.id && item?.state === 'ACTIVE'
  );
  const dailyDeals = activeCampaigns?.find(c => c?.id === DAILY_DEALS_ID);

  const afterUpdateParams = (url: string, routerParams: RouterParams) => {
    history.replace(url);
    dispatch(campaignQuery(routerParams));
  };

  useEffect(() => {
    // Send initial query form url
    dispatch(campaignQuery(router));
  }, []);

  const updateUrlParams = (item: UrlParams) => {
    const { key, value, isGrouped, keep, deleteOffset = true } = item;
    const qs = router.qs;
    const qsKey = qs[key] as string;

    let newValue: string | number | undefined = value;
    if (qsKey && isGrouped) {
      newValue = adjustGroupedUrlParams(
        qsKey,
        String(newValue),
        Boolean(keep),
        categoryTree
      );
    } else if (qs[key] && newValue === qs[key] && !keep) {
      // The value was found in the query string and is equal to the given value, delete it
      newValue = undefined;
      delete qs[key];
    }

    // Remove all filter ranges if the query is changed
    if (key === 'query') {
      Object.keys(router.filterRanges).forEach(filterKey => {
        delete qs[filterKey];
      });

      // Sort on relevance if the query string isn't empty
      qs.sort = newValue ? 'relevance' : 'product.popularity.total';
    }

    let newParams = qs;
    if (newValue) {
      newParams = { ...newParams, [key]: String(newValue) };
    } else {
      // Remove the key if no value exists
      delete qs[key];
    }

    if (deleteOffset) {
      // Remove any potential offset if the url params are updated
      delete newParams.offset;
    }

    const search =
      Object.keys(newParams).length > 0
        ? `?${queryString.encode(newParams)}`
        : '';

    executeSearch(search);
  };

  const executeSearch = (search: string) => {
    const url = history.location.pathname + search;
    const routerParams = buildRouter({
      pathname: history.location.pathname,
      search,
    });

    setRouter(routerParams);
    afterUpdateParams(url, routerParams);
  };

  const clearFilter = (keepSort: boolean) => {
    executeSearch(
      keepSort ? `?${queryString.encode({ sort: router.sort })}` : ''
    );
  };

  if (!campaign?.id) return null;

  return (
    <StyledCampaign>
      {renderMode !== 'native' && <CampaignBreadcrumb campaign={campaign} />}
      <CampaignHeader isActive={activeCampaign} campaign={campaign} />

      {activeCampaign ? (
        <>
          <StyledGutterWrapper>
            <StyledProductWrapper>
              <ErrorBoundary onError={() => null}>
                <CampaignFilters
                  buildRouter={buildRouter}
                  campaign={campaign}
                  categoryTree={categoryTree}
                  clearFilter={clearFilter}
                  executeSearch={executeSearch}
                  router={router}
                  selectedFilters={selectedFilters}
                  setShowFilter={setShowFilter}
                  setStores={setStores}
                  showFilter={showFilter}
                  updateUrlParams={updateUrlParams}
                />
              </ErrorBoundary>

              <StyledProducts>
                <Deals
                  campaignId={campaign.id}
                  router={router}
                  rangeOptions={RANGE_OPTIONS}
                  onChange={item =>
                    updateUrlParams({
                      key: item.id,
                      value: item.value,
                      deleteOffset: item.deleteOffset,
                    })
                  }
                  onClear={() => {
                    clearFilter(false);
                  }}
                />
              </StyledProducts>
            </StyledProductWrapper>
          </StyledGutterWrapper>
          {!campaign?.facetted?.isFacetted && (
            <ErrorBoundary onError={() => null}>
              <CampaignArticles
                featuredArticle={featuredArticle}
                articles={campaign?.articles}
                campaignName={campaign.name}
              />
            </ErrorBoundary>
          )}
        </>
      ) : (
        <>
          <CampaignInactive
            targetDate={
              campaign?.countdown || campaign?.start
                ? Number(campaign?.countdown || campaign?.start)
                : undefined
            }
            campaign={campaign}
            dailyDeals={dailyDeals}
            router={router}
            campaignDeals={campaignDeals}
          />

          <StyledCampaignGrouping
            title={t('campaigns', 'themes.ongoing-campaigns')}
            campaigns={activeCampaigns}
          />
        </>
      )}
    </StyledCampaign>
  );
};

export default CampaignPageLoader;
